import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronRight, CheckCircle, ArrowRight, Clock, AlertTriangle, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import SignalDetectionPanel from '@/components/issues/SignalDetectionPanel';
import { IssueEnrichButton, IssueEnrichmentDisplay } from '@/components/council/IssueEnrichmentPanel';
import PreTriageRejectionPanel from '@/components/council/PreTriageRejectionPanel';

/* ─────────────────────────────────────────────────────────────
   RANKING HELPERS
   Multi-factor score: severity (0–40) + recency (0–30) + seniority (0–30)
   Hierarchy level used only as a tiebreaker on equal scores.
   ───────────────────────────────────────────────────────────── */

const SEVERITY_SCORES = {
  critical: 40,
  high: 30,
  medium: 20,
  low: 10,
};

const RANK_SENIORITY_SCORES = {
  'Commander in Chief': 30,
  'Commander': 25,
  'Vice Commander': 22,
  'Lieutenant Commander': 18,
  'Officer': 15,
  'Junior Officer': 10,
  'Cadet': 5,
};

function getSeverityScore(issue) {
  return SEVERITY_SCORES[issue.severity] ?? 15;
}

function getRecencyScore(issue) {
  const dateStr = issue.reported_at || issue.created_date;
  if (!dateStr) return 0;
  const ageMs = Date.now() - new Date(dateStr).getTime();
  const ageDays = ageMs / (1000 * 60 * 60 * 24);
  /* Decay window: 90 days → score 30 down to 0. Anything older than 90d = 0. */
  const score = Math.max(0, 30 - (ageDays / 90) * 30);
  return Math.round(score);
}

function getSeniorityScore(issue, officers) {
  if (!issue.reported_by) return 0;
  const officer = officers.find((o) => o.user_email === issue.reported_by);
  if (!officer) return 0;
  /* Try rank_title first; fall back to custom_role. */
  const rankKey = officer.rank_title || officer.custom_role || '';
  return RANK_SENIORITY_SCORES[rankKey] ?? 5;
}

function getHierarchyLevel(issue, missions, objectives, activities) {
  if (issue.linked_mission_id && missions.find((m) => m.id === issue.linked_mission_id)) return 0;
  if (issue.linked_objective_id && objectives.find((o) => o.id === issue.linked_objective_id)) return 1;
  if (issue.linked_activity_id && activities.find((a) => a.id === issue.linked_activity_id)) return 2;
  if (issue.source === 'task') return 3;
  return 4;
}

function rankIssues(issues, officers, missions, objectives, activities) {
  return [...issues].sort((a, b) => {
    const scoreA =
      getSeverityScore(a) +
      getRecencyScore(a) +
      getSeniorityScore(a, officers);
    const scoreB =
      getSeverityScore(b) +
      getRecencyScore(b) +
      getSeniorityScore(b, officers);

    if (scoreB !== scoreA) return scoreB - scoreA;

    /* Tiebreaker: structural hierarchy (Mission-linked before unlinked) */
    return (
      getHierarchyLevel(a, missions, objectives, activities) -
      getHierarchyLevel(b, missions, objectives, activities)
    );
  });
}

/* ─────────────────────────────────────────────────────────────
   RANK BADGE — shows priority rank number on each issue card
   ───────────────────────────────────────────────────────────── */
function RankBadge({ rank, score }) {
  const color =
    rank === 1
      ? 'bg-red-500/20 text-red-300 border-red-500/30'
      : rank <= 3
      ? 'bg-orange-500/20 text-orange-300 border-orange-500/30'
      : rank <= 7
      ? 'bg-amber-500/20 text-amber-300 border-amber-500/30'
      : 'bg-slate-700/40 text-slate-400 border-slate-600/30';

  return (
    <div
      className={`flex flex-col items-center justify-center w-9 h-9 rounded-full border text-xs font-bold shrink-0 ${color}`}
      title={`Priority score: ${score}`}
    >
      #{rank}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   MAIN COMPONENT
   ───────────────────────────────────────────────────────────── */

const frequencyOrder = { escalating: 0, recurring: 1, first_occurrence: 2 };

export default function Phase2Triage({ issues, missions, objectives, activities, officers, onProceed, onIssueClassified }) {
  const [expandedIssue, setExpandedIssue] = useState(null);

  const pendingIssues = issues.filter((i) => (!i.classification_gate1 || i.classification_gate1 === 'pending') && i.status !== 'rejected');
  const classifiedIssues = issues.filter((i) => i.classification_gate1 && i.classification_gate1 !== 'pending');
  const rejectedInSession = issues.filter((i) => i.status === 'rejected');

  /* Apply multi-factor ranking to pending issues */
  const sorted = rankIssues(pendingIssues, officers, missions, objectives, activities);

  /* Pre-compute scores for display (rank badge tooltip) */
  const issueScores = {};
  sorted.forEach((issue, idx) => {
    issueScores[issue.id] = {
      rank: idx + 1,
      score:
        getSeverityScore(issue) +
        getRecencyScore(issue) +
        getSeniorityScore(issue, officers),
    };
  });

  const noiseCount = classifiedIssues.filter((i) => i.classification_gate1 === 'noise').length;
  const signalCount = classifiedIssues.filter((i) => i.classification_gate1 === 'signal').length;
  const allDone = pendingIssues.length === 0;

  const getSourceContext = (issue) => {
    const parts = [];
    if (issue.linked_mission_id) {
      const m = missions.find((m) => m.id === issue.linked_mission_id);
      if (m) parts.push(`Mission: ${m.mission_code || m.title}`);
    }
    if (issue.linked_objective_id) {
      const o = objectives.find((o) => o.id === issue.linked_objective_id);
      if (o) parts.push(`Obj: ${o.objective_code || o.statement?.substring(0, 40)}`);
    }
    if (issue.linked_activity_id) {
      const a = activities.find((a) => a.id === issue.linked_activity_id);
      if (a) parts.push(`Act: ${a.activity_code || a.title}`);
    }
    return parts.join(' → ');
  };

  const handleClassified = (issueId) => {
    setExpandedIssue(null);
    onIssueClassified?.(issueId);
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-4">
        <h2 className="text-xl font-bold text-white">Phase 2: Triage — Noise vs Signal</h2>
        <p className="text-sm text-slate-300">15-25 minutes — Classify each issue</p>
        <p className="text-xs text-slate-500 mt-1">Ranked by severity · recency · submitter seniority</p>
      </div>

      {/* Running Tally */}
      <div className="flex items-center justify-center gap-6 p-3 rounded-lg bg-slate-800/60 border border-slate-700">
        <span className="text-sm text-slate-300">
          Processed: <span className="font-bold text-white">{classifiedIssues.length + rejectedInSession.length}/{classifiedIssues.length + pendingIssues.length + rejectedInSession.length}</span>
        </span>
        <span className="text-sm text-slate-400">|</span>
        <span className="text-sm text-slate-200">Noise: <span className="font-bold text-slate-200">{noiseCount}</span></span>
        <span className="text-sm text-slate-200">Signal: <span className="font-bold text-amber-300">{signalCount}</span></span>
        {rejectedInSession.length > 0 && <span className="text-sm text-red-300">Rejected: <span className="font-bold text-red-300">{rejectedInSession.length}</span></span>}
      </div>

      {/* Issue Queue */}
      <div className="space-y-2">
        {sorted.map((issue) => {
          const isExpanded = expandedIssue === issue.id;
          const sourceCtx = getSourceContext(issue);
          const reporter = officers.find((o) => o.user_email === issue.reported_by);
          const { rank, score } = issueScores[issue.id] || { rank: 0, score: 0 };

          return (
            <Card key={issue.id} className="glass-panel border-slate-800">
              <Collapsible open={isExpanded} onOpenChange={() => setExpandedIssue(isExpanded ? null : issue.id)}>
                <CollapsibleTrigger className="w-full">
                  <CardHeader className="py-3 cursor-pointer hover:bg-slate-800/30 transition-colors">
                    <div className="flex items-center gap-3">
                      {/* Rank badge */}
                      <RankBadge rank={rank} score={score} />

                      {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                      <div className="flex-1 text-left">
                        <p className="text-[10px] text-slate-500 font-mono select-text cursor-text" onClick={(e) => e.stopPropagation()}>{issue.id}</p>
                        <p className="text-slate-700 text-sm font-medium">{issue.title}</p>
                        {sourceCtx && <p className="text-xs text-slate-500 mt-0.5">{sourceCtx}</p>}
                        <p className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {issue.reported_at ? format(new Date(issue.reported_at), 'MMM d, yyyy') : issue.created_date ? format(new Date(issue.created_date), 'MMM d, yyyy') : '-'}
                          {reporter && (
                            <span className="ml-2 text-slate-500">· {reporter.callsign}{reporter.rank_title ? ` (${reporter.rank_title})` : ''}</span>
                          )}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Badge className="bg-amber-500/20 text-amber-600 px-2.5 py-0.5 text-xs font-semibold rounded-md inline-flex items-center border transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent shadow hover:bg-primary/80">{issue.severity}</Badge>
                        {issue.frequency_signal && issue.frequency_signal !== 'first_occurrence' &&
                          <Badge className="bg-purple-500/20 text-purple-300">{issue.frequency_signal}</Badge>
                        }
                      </div>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="pt-0 space-y-4">
                    {issue.description &&
                      <p className="text-slate-600 text-sm">{issue.description}</p>
                    }
                    <div className="flex flex-wrap gap-2 text-xs">
                      {reporter && <Badge variant="outline" className="text-slate-700 px-2.5 py-0.5 text-xs font-semibold rounded-md inline-flex items-center border transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-slate-600">By: {reporter.callsign}</Badge>}
                      {issue.category && <Badge variant="outline" className="text-slate-700 px-2.5 py-0.5 text-xs font-semibold rounded-md inline-flex items-center border transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-slate-600">{issue.category.replace('_', ' ')}</Badge>}
                    </div>
                    {/* Tags */}
                    {(issue.tagged_officers?.length > 0 || issue.tagged_artifacts?.length > 0 || issue.tagged_policies?.length > 0 || issue.tagged_capabilities?.length > 0) &&
                      <div className="flex flex-wrap gap-1">
                        {(issue.tagged_officers || []).map((t) => <Badge key={t} className="bg-indigo-500/20 text-indigo-300 text-[10px]">👤 {t}</Badge>)}
                        {(issue.tagged_artifacts || []).map((t) => <Badge key={t} className="bg-cyan-500/20 text-cyan-300 text-[10px]">📄 {t}</Badge>)}
                        {(issue.tagged_policies || []).map((t) => <Badge key={t} className="bg-amber-500/20 text-amber-300 text-[10px]">📋 {t}</Badge>)}
                        {(issue.tagged_capabilities || []).map((t) => <Badge key={t} className="bg-emerald-500/20 text-emerald-300 text-[10px]">⚡ {t}</Badge>)}
                      </div>
                    }
                    <div className="border-t border-slate-700 pt-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">Signal Detection</span>
                        <PreTriageRejectionPanel issue={issue} officers={officers} onRejected={handleClassified} />
                      </div>
                      <SignalDetectionPanel issue={issue} onComplete={() => handleClassified(issue.id)} />
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          );
        })}
      </div>

      {/* Classified issues collapsed */}
      {classifiedIssues.length > 0 &&
        <div className="space-y-1">
          <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider mb-2">Classified in this session</p>
          {classifiedIssues.map((issue) =>
            <div key={issue.id} className="flex items-center gap-2 p-2 rounded bg-slate-800/30">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              <div className="flex-1">
                <p className="text-[10px] text-slate-500 font-mono select-text cursor-text">{issue.id}</p>
                <span className="text-sm text-slate-300">{issue.title}</span>
                <p className="text-[10px] text-slate-500 mt-0.5">
                  {issue.reported_at ? format(new Date(issue.reported_at), 'MMM d, yyyy') : issue.created_date ? format(new Date(issue.created_date), 'MMM d, yyyy') : ''}
                </p>
              </div>
              <Badge className={issue.classification_gate1 === 'noise' ? 'bg-slate-500/20 text-slate-200' : 'bg-amber-500/20 text-amber-300'}>
                {issue.classification_gate1}
              </Badge>
            </div>
          )}
        </div>
      }

      {!allDone && (
        <div className="p-3 rounded-lg bg-amber-950/30 border border-amber-500/30 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-amber-300 font-medium">{pendingIssues.length} issue{pendingIssues.length !== 1 ? 's' : ''} still pending triage</p>
            <p className="text-xs text-amber-400/70 mt-0.5">Unclassified issues will remain in the queue for the next session.</p>
          </div>
        </div>
      )}

      <Button onClick={onProceed} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-12 text-base">
        {allDone ? 'Proceed to Density Assessment' : 'Proceed Anyway — Skip Remaining'}
        <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
    </div>
  );
}
